<template>
  <main-header />
  <section class="py-5 bg-light">
    <div class="container">
      <div class="row mb-4">
        <div class="col-12 text-center">
          <h1 class="display-5 fw-bold text-dark">My Account</h1>
          <p class="text-muted">Manage your profile and view your order history</p>
        </div>
      </div>

      <div class="row g-4">
        <!-- Profile Info Section -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body text-center p-4">
              <div class="mb-3">
                <div class="avatar-placeholder rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center fs-2" style="width: 80px; height: 80px;">
                  {{ user?.displayName ? user.displayName.charAt(0).toUpperCase() : (user?.email ? user.email.charAt(0).toUpperCase() : 'U') }}
                </div>
              </div>
              <h3 class="card-title h4 mb-1">{{ user?.displayName || "Guest User" }}</h3>
              <p class="text-muted mb-4">{{ user?.email }}</p>
              
              <div v-if="!user" class="text-center">
                 <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Past Orders Section -->
        <div class="col-lg-8">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-bottom-0 pt-4 px-4 pb-2 d-flex justify-content-between align-items-center">
              <h3 class="h4 mb-0">Order History</h3>
              <div class="d-flex align-items-center gap-2">
                <button class="btn btn-outline-secondary btn-sm" @click="fetchOrders(true)" title="Refresh orders">
                  <i class="bi bi-arrow-clockwise" :class="{ 'spin-anim': loadingOrders }"></i> Refresh
                </button>
                <span class="badge bg-secondary rounded-pill">{{ orders.length }} Orders</span>
              </div>
            </div>
            <div class="card-body px-4 pb-4">
              
              <div v-if="loadingOrders" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2 text-muted">Fetching your orders...</p>
              </div>

              <div v-else-if="orders.length === 0" class="text-center py-5">
                <div class="mb-3 text-muted">
                  <i class="bi bi-bag-x fs-1"></i>
                </div>
                <h5 class="text-muted">No orders found</h5>
                <p class="mb-4">Looks like you haven't placed any orders yet.</p>
                <router-link to="/shop" class="btn btn-primary px-4 rounded-pill">Start Shopping</router-link>
              </div>

              <div v-else class="order-list">
                <div v-for="order in orders" :key="order.id" class="card mb-3 border hover-shadow transition-all">
                  <div class="card-body p-0">
                    <div class="p-3 d-flex flex-wrap align-items-center justify-content-between border-bottom bg-light bg-opacity-10 rounded-top">
                      <div class="d-flex flex-column">
                        <span class="small text-muted text-uppercase fw-bold">Order Placed</span>
                        <span class="fw-medium">{{ formatDate(order.timeOrderedTs || order.timeOrdered) }}</span>
                      </div>
                      <div class="d-flex flex-column">
                         <span class="small text-muted text-uppercase fw-bold">Total</span>
                         <span class="fw-medium">${{ formatPrice(order.total) }}</span>
                      </div>
                      <div class="d-flex flex-column">
                         <span class="small text-muted text-uppercase fw-bold">Order #</span>
                         <span class="font-monospace text-primary cursor-pointer" @click="openOrderDetails(order)">{{ order.id.substring(0, 8).toUpperCase() }}</span>
                      </div>
                      <div class="mt-2 mt-md-0">
                        <button class="btn btn-outline-primary btn-sm rounded-pill" @click="openOrderDetails(order)">
                          View Details
                        </button>
                      </div>
                    </div>
                    
                    <div class="p-3">
                      <div class="row align-items-center">
                        <div class="col-md-8">
                          <div class="d-flex align-items-center">
                            <div class="status-indicator me-3" :class="getStatusColor(order.status)"></div>
                            <div>
                              <h6 class="mb-1 text-capitalize">{{ formatStatus(order.status) }}</h6>
                              <p class="small text-muted mb-0">
                                {{ order.order ? order.order.length : 0 }} item(s)
                              </p>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4 mt-3 mt-md-0">
                           <div class="d-flex gap-2 overflow-hidden">
                              <!-- Preview images if available -->
                              <template v-for="(item, idx) in (order.order || []).slice(0, 3)" :key="idx">
                                <div class="bg-light rounded border d-flex align-items-center justify-content-center text-muted small position-relative overflow-hidden" style="width: 40px; height: 40px; min-width: 40px;">
                                   <img v-if="item.image" :src="item.image" :alt="item.name" class="w-100 h-100 object-fit-cover" />
                                   <!-- Use a generic icon if no image -->
                                   <span v-else style="font-size: 10px;">{{ item.name ? item.name.substring(0, 2).toUpperCase() : '?' }}</span>
                                </div>
                              </template>
                              <div v-if="(order.order || []).length > 3" class="bg-light rounded border d-flex align-items-center justify-content-center text-muted small" style="width: 40px; height: 40px; min-width: 40px;">
                                +{{ order.order.length - 3 }}
                              </div>
                           </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for displaying order details -->
    <div v-if="showOrderModal" class="modal-backdrop fade show"></div>
    <div v-if="showOrderModal" class="modal fade show d-block" tabindex="-1" role="dialog" @click.self="closeOrderDetails">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold">Order Details</h5>
            <button type="button" class="btn-close" @click="closeOrderDetails" aria-label="Close"></button>
          </div>
          
          <div v-if="selectedOrder" class="modal-body p-0">
            <!-- Order Summary Header -->
            <div class="p-4 border-bottom bg-white">
               <div class="row g-3">
                 <div class="col-sm-6">
                    <h6 class="text-muted text-uppercase small fw-bold mb-1">Order ID</h6>
                    <p class="font-monospace mb-0 select-all">{{ selectedOrder.id }}</p>
                 </div>
                 <div class="col-sm-6 text-sm-end">
                    <h6 class="text-muted text-uppercase small fw-bold mb-1">Date Placed</h6>
                    <p class="mb-0">{{ formatDate(selectedOrder.timeOrderedTs || selectedOrder.timeOrdered) }}</p>
                 </div>
               </div>
            </div>

            <!-- Order Tracking Stepper -->
            <div class="p-4 border-bottom bg-light overflow-hidden">
               <h6 class="fw-bold mb-4">Order Status</h6>
               <div class="position-relative d-flex justify-content-between text-center mx-2 mx-md-4">
                  <!-- Progress Bar Background -->
                  <div class="position-absolute bg-secondary bg-opacity-25" style="height: 4px; top: 14px; left: 10%; width: 80%; z-index: 1;"></div>
                  
                  <!-- Progress Bar Fill -->
                  <div class="position-absolute bg-primary transition-all" 
                       :style="{ width: getTrackingProgress(selectedOrder.status) + '%', height: '4px', top: '14px', left: '10%', zIndex: 1 }">
                  </div>

                  <!-- Steps -->
                  <div v-for="(step, index) in trackingSteps" :key="index" class="position-relative z-2" style="width: 20%;">
                     <div class="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 transition-all position-relative"
                          :class="getStepCircleClass(selectedOrder.status, index)"
                          style="width: 28px; height: 28px; border: 3px solid #f8f9fa;">
                        <i v-if="isStepCompleted(selectedOrder.status, index)" class="bi bi-check text-white" style="font-size: 1.2rem;"></i>
                        <span v-else class="small fw-bold" :class="isStepActive(selectedOrder.status, index) ? 'text-white' : 'text-muted'">{{ index + 1 }}</span>
                     </div>
                     <p class="small mb-0 fw-bold text-uppercase d-none d-sm-block" 
                        :class="isStepActiveOrCompleted(selectedOrder.status, index) ? 'text-primary' : 'text-muted'" 
                        style="font-size: 10px; letter-spacing: 0.5px;">{{ step }}</p>
                     <!-- Mobile Step Label (Only current or active) -->
                      <p class="small mb-0 fw-bold text-uppercase d-block d-sm-none position-absolute start-50 translate-middle-x w-100" 
                        v-if="isStepActive(selectedOrder.status, index)"
                        :class="isStepActiveOrCompleted(selectedOrder.status, index) ? 'text-primary' : 'text-muted'" 
                        style="font-size: 9px; top: 32px;">{{ step }}</p>
                  </div>
               </div>
            </div>

            <div class="p-4">
              <h6 class="fw-bold mb-3 border-bottom pb-2">Items Ordered</h6>
              
              <div v-for="(item, index) in selectedOrder.order" :key="index" class="card mb-3 border-0 bg-light">
                <div class="card-body">
                  <div class="d-flex gap-3">
                    <!-- Item Image -->
                    <div class="flex-shrink-0" style="width: 60px; height: 60px;">
                      <div class="w-100 h-100 bg-white rounded border d-flex align-items-center justify-content-center overflow-hidden">
                        <img v-if="item.image" :src="item.image" :alt="item.name" class="w-100 h-100 object-fit-cover" />
                        <span v-else class="text-muted small fw-bold">{{ item.name ? item.name.substring(0, 2).toUpperCase() : '?' }}</span>
                      </div>
                    </div>
                    
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <h6 class="fw-bold mb-0 text-primary">{{ item.product || item.name || 'Unknown Item' }}</h6>
                        <span class="badge bg-white text-dark border">{{ item.quantity || 1 }} x ${{ formatPrice(item.price) }}</span>
                      </div>
                      
                      <div class="small text-muted">
                        <div class="row g-2">
                           <div class="col-6" v-if="item.petName"><strong>Pet:</strong> {{ item.petName }} ({{ item.petSpecies }})</div>
                           <div class="col-6" v-if="item.profile"><strong>Profile:</strong> {{ item.profile }}</div>
                           <div class="col-6" v-if="item.controlType"><strong>Control:</strong> {{ item.controlType }}</div>
                           <div class="col-12" v-if="item.comments"><strong>Note:</strong> {{ item.comments }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mt-4">
                <div class="col-md-6 mb-3 mb-md-0">
                  <h6 class="fw-bold mb-3 border-bottom pb-2">Shipping Address</h6>
                  <address class="small text-muted mb-0 ps-2 border-start border-3 border-primary" v-if="selectedOrder.address">
                    <strong>{{ selectedOrder.name }}</strong><br>
                    {{ selectedOrder.address.streetAddress }}<br>
                    {{ selectedOrder.address.city }}, {{ selectedOrder.address.state }} {{ selectedOrder.address.zip }}<br>
                    {{ selectedOrder.address.country }}
                  </address>
                  <p v-else class="small text-muted">No address provided</p>
                </div>
                <div class="col-md-6">
                   <h6 class="fw-bold mb-3 border-bottom pb-2">Order Summary</h6>
                   <div class="d-flex justify-content-between mb-2 small">
                     <span>Subtotal</span>
                     <span>${{ formatPrice(selectedOrder.total) }}</span>
                   </div>
                   <div class="d-flex justify-content-between mb-2 small">
                     <span>Shipping</span>
                     <span>{{ getShippingDisplay(selectedOrder) }}</span>
                   </div>
                   <div class="d-flex justify-content-between border-top pt-2 fw-bold">
                     <span>Total</span>
                     <span class="text-primary">${{ formatPrice(selectedOrder.total) }}</span>
                   </div>
                </div>
              </div>
            </div>

          </div>
          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-secondary" @click="closeOrderDetails">Close</button>
          </div>
        </div>
      </div>
    </div>

  </section>
  <main-footer />
</template>

<script>
import MainHeader from "@/components/MainHeader.vue";
import MainFooter from "@/components/MainFooter.vue";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "@/firebase/init";
import { inject } from 'vue';

import productsData from "../../../public/data/products.json";
import individualProductsData from "../../../public/data/individual_products.json";

export default {
  components: {
    MainHeader,
    MainFooter,
  },
  setup() {
    const swal = inject('$swal');
    return { swal };
  },
  data() {
    return {
      user: null,
      orders: [], 
      loadingOrders: true, 
      loggedInEmail: null, 
      showOrderModal: false, 
      selectedOrder: null, 
      paymentJustCompleted: false,
      pendingPaymentStatus: null,
      checkoutSessionId: null,
      products: productsData.results,
      individualProducts: individualProductsData,
      trackingSteps: ['design', 'production', 'shipped', 'warehouse', 'delivered']
    };
  },
  methods: {
    getTrackingProgress(status) {
        if (!status) return 0;
        const currentStepIndex = this.trackingSteps.indexOf(status.toLowerCase());
        if (currentStepIndex === -1) {
             if (status === 'paid' || status === 'pending_payment') return 0;
             return 0;
        }
        return (currentStepIndex / (this.trackingSteps.length - 1)) * 80;
    },
    isStepCompleted(status, index) {
        if (!status) return false;
        const currentStepIndex = this.trackingSteps.indexOf(status.toLowerCase());
        return currentStepIndex > index;
    },
    isStepActive(status, index) {
        if (!status) return false;
        const currentStepIndex = this.trackingSteps.indexOf(status.toLowerCase());
        return currentStepIndex === index;
    },
    isStepActiveOrCompleted(status, index) {
        if (!status) return false;
        const currentStepIndex = this.trackingSteps.indexOf(status.toLowerCase());
        return currentStepIndex >= index;
    },
    getStepCircleClass(status, index) {
        if (this.isStepCompleted(status, index)) return 'bg-success';
        if (this.isStepActive(status, index)) return 'bg-primary';
        return 'bg-secondary bg-opacity-25';
    },
    formatDate(timestamp) {
      if (!timestamp) return 'Unknown Date';
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }).format(date);
    },
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    },
    formatStatus(status) {
      if (!status) return 'Processing';
      return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    getStatusColor(status) {
      const map = {
        'pending_payment': 'bg-warning',
        'paid': 'bg-success',
        'design': 'bg-info',
        'production': 'bg-primary',
        'shipped': 'bg-primary',
        'warehouse': 'bg-warning',
        'delivered': 'bg-success',
        'cancelled': 'bg-danger'
      };
      return map[status] || 'bg-secondary';
    },
    getTrackingStep(status) {
        const steps = ['design', 'production', 'shipped', 'warehouse', 'delivered'];
        if (!status) return 0;
        const index = steps.indexOf(status.toLowerCase());
        return index !== -1 ? index + 1 : 0;
    },
    hasPortraitStyle(item) {
        if (!item) return false;
        if (item.portraitStyle) return true;
        if (item.userSelections?.portraitStyle) return true;
        if (Array.isArray(item.personalization)) {
             return item.personalization.some(entry => {
                 if (entry.input && typeof entry.input === 'object' && entry.input.portraitStyle) return true;
                 if (Array.isArray(entry.input) && entry.input.some(i => i && i.portraitStyle)) return true;
                 return false;
             });
        }
        return false;
    },
    getShippingDisplay(order) {
      if (!order || !order.order) return 'Free';
      
      let itemsBaseTotal = 0;
      let portraitFees = 0;

      order.order.forEach(item => {
        const qty = item.quantity || 1;
        const price = parseFloat(item.price || 0);
        itemsBaseTotal += price * qty;

        // Use saved count if available (new orders), or fallback to detection (old orders)
        // Use 'portraitCount' as that's what we save in CheckoutView
        if (typeof item.portraitCount === 'number') {
            portraitFees += item.portraitCount * 5 * qty;
        } else if (this.hasPortraitStyle(item)) {
            portraitFees += 5 * qty;
        }
      });
      
      const orderTotal = parseFloat(order.total || 0);
      
      // Calculate remaining difference: (Shipping - Discount)
      // If result > 0, it's likely the shipping fee (partially discounted or full)
      const diff = orderTotal - (itemsBaseTotal + portraitFees);
      
      if (diff > 0.01) {
        // Heuristic fallback for legacy orders where portraitCount wasn't saved:
        // If diff looks like "18 + N*5" or "25 + N*5", assume shipping is 18 or 25.
        // This handles cases like $23 total surcharge (18 ship + 5 portrait)
        
        // Check Express ($28) + Portrait Fees (implied)
        const remainder28 = diff - 28;
        if (remainder28 >= 0 && Math.abs(remainder28 % 5) < 0.01) {
            return '$28.00';
        }

        // Check Standard ($18) + Portrait Fees (implied)
        const remainder18 = diff - 18;
        if (remainder18 >= 0 && Math.abs(remainder18 % 5) < 0.01) {
            return '$18.00';
        }

        return '$' + diff.toFixed(2);
      }
      
      return 'Free';
    },
    openOrderDetails(order) {
      this.selectedOrder = order;
      this.showOrderModal = true;
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    },
    closeOrderDetails() {
      this.showOrderModal = false;
      this.selectedOrder = null; 
      document.body.style.overflow = '';
    },
    async fetchOrders(showLoading = false) {
      if (!this.user || !this.user.uid) return;
      
      if (showLoading) {
        this.loadingOrders = true;
      }

      try {
        console.log("🔵 Fetching orders for UID:", this.user.uid);
        
        // Query by accountUid (more reliable)
        let ordersQuery = query(
          collection(db, "orders"),
          where("accountUid", "==", this.user.uid)
        );
        
        let querySnapshot = await getDocs(ordersQuery);
        
        // Fallback to email if no orders found by UID (for older orders)
        if (querySnapshot.empty) {
           console.log("⚠️ No orders found by UID, trying email:", this.user.email);
           ordersQuery = query(
              collection(db, "orders"),
              where("accountEmail", "==", this.user.email)
           );
           querySnapshot = await getDocs(ordersQuery);
        }
        
          console.log("🔵 Orders found:", querySnapshot.size);
          
          this.orders = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            // Process items to add images from local JSON data
            if (data.order && Array.isArray(data.order)) {
              data.order = data.order.map(item => {
                // Try to find image in products.json or individual_products.json
                let imageUrl = null;
                
                // Check if it's a set product
                const setProduct = this.products.find(p => p.name === item.name || p.name === item.product);
                if (setProduct && setProduct.images && setProduct.images.length > 0) {
                  imageUrl = setProduct.images[0];
                }
                
                // If not found, check individual products
                if (!imageUrl) {
                  const individualProduct = this.individualProducts.find(p => p.name === item.name || p.name === item.product);
                  if (individualProduct && individualProduct.images && individualProduct.images.length > 0) {
                    imageUrl = individualProduct.images[0];
                  }
                }
                
                // If still not found, use uploaded image if available (for custom items)
                if (!imageUrl && item.image) {
                   imageUrl = item.image;
                }

                return {
                  ...item,
                  image: imageUrl 
                };
              });
            }
            
            return {
              id: doc.id,
              ...data,
            };
          })
          .sort((a, b) => {
          const dateA = a.timeOrderedTs || 0;
          const dateB = b.timeOrderedTs || 0;
          return dateB - dateA;
        });
        
        this.loadingOrders = false;
        
      } catch (error) {
        console.error("❌ Error fetching orders:", error);
        this.loadingOrders = false;
      }
    }
  },
  async mounted() {
    // Check for payment success/failure in URL
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    this.pendingPaymentStatus = paymentStatus;
    this.checkoutSessionId = sessionId;
    
    if (paymentStatus === 'failed') {
      if (this.swal) {
        this.swal({
          icon: 'error',
          title: 'Payment Failed',
          text: 'Your payment could not be processed. Please try again.',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#d33',
        });
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log("✅ User logged in:", user.email);
        this.loggedInEmail = user.email;

        this.user = {
          email: user.email,
          displayName: user.displayName || "Not provided",
          uid: user.uid
        };

        // Fetch orders using the new method
        await this.fetchOrders(true);
          
        // Handle payment success logic
        if (this.pendingPaymentStatus === 'success' && this.checkoutSessionId) {
          console.log("💳 Processing payment success for session:", this.checkoutSessionId);
          
          const sessionIdToMatch = this.checkoutSessionId.replace('cs_test_', '').replace('cs_live_', '');
          console.log("🔍 Looking for order with session ID containing:", sessionIdToMatch);
          
          // Find order matching this specific checkout session
          let matchingOrder = this.orders.find(order => {
            const orderSessionId = order.checkoutSessionId || '';
            return orderSessionId.includes(sessionIdToMatch) || orderSessionId === this.checkoutSessionId;
          });
          
          // If not found, wait and retry
          if (!matchingOrder) {
            console.log("⏳ Order not found yet, waiting 3 seconds...");
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            // Refetch orders using the method
            await this.fetchOrders();
            
            // Try to find matching order again
            matchingOrder = this.orders.find(order => {
              const orderSessionId = order.checkoutSessionId || '';
              return orderSessionId.includes(sessionIdToMatch) || orderSessionId === this.checkoutSessionId;
            });
          }
          
          if (matchingOrder) {
            // Payment successful for THIS specific session
            console.log("✅ Payment successful for order:", matchingOrder.id);
            
            // Clear cart
            this.$store.dispatch('clear_cart');
            
            // Clear journey data (if any)
            const cartItems = this.$store.state.cart || [];
            cartItems.forEach(item => {
              if (item._id) {
                this.$store.dispatch('delete_journey', item._id);
              }
            });
            
            this.paymentJustCompleted = true;
            
            // Show success popup
            if (this.swal) {
              this.swal({
                icon: 'success',
                title: 'Payment Successful!',
                text: `Order #${matchingOrder.id.substring(0, 8).toUpperCase()} confirmed! Check your email for details.`,
                confirmButtonText: 'View Order',
                confirmButtonColor: '#008765',
              });
            }
          } else {
            // Order for this specific session not found
            console.log("⚠️ Order not created yet for session:", this.checkoutSessionId);
            if (this.swal) {
              this.swal({
                icon: 'info',
                title: 'Processing Payment...',
                text: 'Your payment is being processed. Please wait a moment and refresh.',
                confirmButtonText: 'Refresh Page',
                confirmButtonColor: '#2f4858',
              }).then((result) => {
                if (result.isConfirmed) {
                  window.location.reload();
                }
              });
            }
          }
          
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
          this.pendingPaymentStatus = null;
          this.checkoutSessionId = null;
        }
        
      } else {
        this.user = null;
        this.orders = [];
        this.loadingOrders = false;
      }
    });
  },
};
</script>

<style scoped>
.hover-shadow:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.transition-all {
  transition: all 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.avatar-placeholder {
  font-weight: bold;
}

.spin-anim {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Ensure modal displays correctly over everything */
.modal-backdrop {
  z-index: 1040;
}
.modal {
  z-index: 1050;
  background: rgba(0,0,0,0.5);
}

.select-all {
  user-select: all;
}
.object-fit-cover {
  object-fit: cover;
}
</style>
